import Big from 'big.js';
import { useProductList } from 'helpers/hooks/useProductList';
import { useState } from 'react';
import { DiscountType, JsonDataItem, ServiceProduct } from 'types';

type ServicePrice = {
  subTotal: string;
  gst: string;
  total: string;
  partsTotal: string;
  productsTotal: string;
  legacyProductsTotal: string;
  discountTotal: string;
};

type Totals = {
  subTotal: Big;
  gst: Big;
  total: Big;
};

type UpdatePriceParams = {
  job: {
    tax_rate?: number;
    labour_rate_inc_gst: number;
  };
  legacy_products?: any;
  products?: ServiceProduct[];
  parts?: any;
  discounts?: any;
};

const systemProductIds = [8, 9, 10]; // WS, SDS, PHS

export const useServicePrice = (customerId?: string, serviceId?: string) => {
  const [servicePrice, setServicePrice] = useState<ServicePrice>({
    subTotal: '0.00',
    gst: '0.00',
    total: '0.00',
    partsTotal: '0.00',
    productsTotal: '0.00',
    legacyProductsTotal: '0.00',
    discountTotal: '0.00',
  });

  const [productList] = useProductList(customerId, serviceId);

  const updateServicePrice = (data: UpdatePriceParams) => {
    const taxRate = data.job?.tax_rate ? Big(data.job.tax_rate).div(100) : Big(0.1);
    const labourRate = Big(data.job?.labour_rate_inc_gst);

    const initialTotals: Totals = {
      subTotal: Big(0),
      gst: Big(0),
      total: Big(0),
    };

    // Legacy Products
    const {
      subTotal: legacyProductsSubTotal,
      gst: legacyProductsGst,
      total: legacyProductsTotal,
    } = data.legacy_products?.reduce((totals: Totals, product: JsonDataItem) => {
      const subTotalProduct = Big(product.price).times(Big(1).minus(taxRate));
      const gstProduct = Big(product.price).times(taxRate);
      const totalProduct = Big(product.price);

      return {
        subTotal: totals.subTotal.plus(subTotalProduct),
        gst: totals.gst.plus(gstProduct),
        total: totals.total.plus(totalProduct),
      };
    }, initialTotals) || initialTotals;

    // Products
    const {
      subTotal: productsSubTotal,
      gst: productsGst,
      total: productsTotal,
    } = data.products?.reduce((totals: Totals, product) => {
      const qty = Big(product.quantity);
      const productDetails = productList?.find((p) => p.ID === product.productId) || {
        sub_total: product.product?.subTotal || 0,
        tax_applied: product.product?.taxApplied || 0,
        total: product.product?.total || 0,
      };

      const subTotalProduct = Big(productDetails.sub_total).times(qty);
      const gstProduct = Big(productDetails.tax_applied).times(qty);
      const totalProduct = Big(productDetails.total).times(qty);

      return {
        subTotal: totals.subTotal.plus(subTotalProduct),
        gst: totals.gst.plus(gstProduct),
        total: totals.total.plus(totalProduct),
      };
    }, initialTotals) || initialTotals;

    // Parts
    const {
      subTotal: partsSubTotal,
      gst: partsGst,
      total: partsTotal,
    } = data.parts?.reduce((totals: Totals, part: JsonDataItem) => {
      // BE provide different data structure for applied and avaialble parts...
      const partPrice = part.details?.price || part.details?.retail;
      const partTax = part.details?.tax_applied || part.details?.tax_retail_applied;
      const partTotal = part.details?.total_price || part.details?.total_retail;
      const qty = Big(part.qty);
      const customPrice = Big(part.price || 0); // for not existing part

      const subTotalPart = part.details?.ID
        ? Big(partPrice).times(qty)
        : Big(customPrice).times(Big(1).minus(taxRate)).times(qty);
      const gstPart = Big(partTax || customPrice.times(taxRate)).times(qty);
      const totalPart = Big(partTotal || customPrice).times(qty);

      return {
        subTotal: totals.subTotal.plus(subTotalPart),
        gst: totals.gst.plus(gstPart),
        total: totals.total.plus(totalPart),
      };
    }, initialTotals) || initialTotals;

    // Discounts
    const hourlyDiscountAmount =
      data.discounts
        ?.filter((disc: JsonDataItem) => disc.type === DiscountType.Time)
        .reduce((total: Big, discount: JsonDataItem) => {
          return total.plus(labourRate.times(discount.amount));
        }, Big(0)) || Big(0);

    const legacyDiscountableTotal =
      data.legacy_products
        ?.filter((product: JsonDataItem) => product.id.includes('consultation'))
        .reduce((total: Big, item: JsonDataItem) => total.plus(new Big(item.price)), new Big(0)) || null;

    const discountableTotal =
      data.products
        ?.filter((product) => !systemProductIds.includes(Number(product.id)))
        .reduce(
          (total: Big, item) =>
            total.plus(
              new Big(productList?.find((p) => p.ID === item.id)?.total || item.product?.total || 0).times(
                item.quantity,
              ),
            ),
          new Big(0),
        ) || null;

    const totalForPercentDiscount = (legacyDiscountableTotal || discountableTotal || Big(0)).minus(
      hourlyDiscountAmount,
    );

    const discountTotal =
      data.discounts?.reduce((total: Big, discount: JsonDataItem) => {
        const { amount, type } = discount;
        switch (type) {
          case DiscountType.Dollar:
            return total.plus(Big(amount));
          case DiscountType.Percent:
            return total.plus(totalForPercentDiscount.times(Big(amount).div(100)));
          case DiscountType.Time:
            return total.plus(labourRate.times(amount));
          default:
            return total;
        }
      }, Big(0)) || Big(0);

    const serviceTotal = productsTotal.plus(legacyProductsTotal).plus(partsTotal).minus(discountTotal);

    setServicePrice({
      productsTotal: Big(productsTotal).lt(0) ? '0.00' : productsTotal.toFixed(2),
      legacyProductsTotal: Big(legacyProductsTotal).lt(0) ? '0.00' : legacyProductsTotal.toFixed(2),
      partsTotal: partsTotal.toFixed(2),
      discountTotal: discountTotal.toFixed(2),
      subTotal: productsSubTotal.plus(legacyProductsSubTotal).plus(partsSubTotal).toFixed(2),
      gst: productsGst.plus(legacyProductsGst).plus(partsGst).toFixed(2),
      total: Big(serviceTotal).lt(0) ? '0.00' : serviceTotal.toFixed(2),
    });
  };

  return { servicePrice, updateServicePrice };
};
